import { useEffect, useState } from 'react';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoTransferOfCareFields } from '@/lib/demo-data';
import { useAuthStore } from '@/lib/auth-store';
import { SignaturePad } from '@/components/SignaturePad';
import type { EPCR, Signature } from '@/types';
import { cn } from '@/lib/cn';
import {
  Users, Check, Clock, UserCheck, PenLine, AlertTriangle,
  Printer, Trash2, X, ClipboardList,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { TransferOfCareScript } from '@/components/TransferOfCareScript';
import { UpstreamEcho } from '@/components/UpstreamEcho';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

const LICENSURE_OPTIONS = ['MD', 'DO', 'APRN', 'PA', 'RN', 'Paramedic', 'RRT', 'Other'];
const ROLE_OPTIONS = [
  'Emergency Physician',
  'Emergency Nurse',
  'Charge Nurse',
  'Hospitalist',
  'Intensivist',
  'Cardiac Cath Team',
  'Stroke Team',
  'Trauma Team',
  'Paramedic (Receiving Unit)',
  'Other',
];

/**
 * Transfer of Care workflow (spec §10).
 * Captures receiving clinician identity, licensure, signature, and time.
 * Handles delayed delivery documentation.
 */
export function TransferOfCareTab({ epcr, onUpdate }: Props) {
  const { user } = useAuthStore();
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [showSignaturePad, setShowSignaturePad] = useState<Signature['signer_role'] | null>(null);
  const [newSigner, setNewSigner] = useState({
    signer_name: '',
    signer_licensure: '',
    signer_role_title: '',
  });
  const [showTocScript, setShowTocScript] = useState(false);

  const isTransport = epcr.transport_decision === 'transport_this_unit' || epcr.transport_decision === 'transfer_other_unit';
  const isRefusal = epcr.transport_decision === 'refusal_care' || epcr.transport_decision === 'refusal_transport';

  useEffect(() => {
    loadSignatures();
  }, [epcr.id]);

  async function loadSignatures() {
    const sigs = await db.signatures.where('epcr_id').equals(epcr.id).toArray();
    setSignatures(sigs);
  }

  async function saveSignature(dataUrl: string, role: Signature['signer_role']) {
    if (!user) return;
    const sig: Signature = {
      id: crypto.randomUUID(),
      epcr_id: epcr.id,
      fire_report_id: null,
      agency_id: epcr.agency_id,
      signer_role: role,
      signer_name: newSigner.signer_name,
      signer_licensure: newSigner.signer_licensure || null,
      signer_role_title: newSigner.signer_role_title || null,
      signature_image_data_url: dataUrl,
      signed_at: new Date().toISOString(),
      ip_address: null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      created_at: new Date().toISOString(),
    };
    await db.signatures.add(withSyncMeta(sig));
    await enqueueSync('signatures', sig.id, 'insert', sig as unknown as Record<string, unknown>);
    setShowSignaturePad(null);
    setNewSigner({ signer_name: '', signer_licensure: '', signer_role_title: '' });
    await loadSignatures();

    // Mark TOC completed when receiving clinician signs
    if (role === 'receiving_clinician' && !epcr.toc_completed) {
      onUpdate({
        toc_completed: true,
        toc_completed_at: new Date().toISOString(),
        toc_receiving_facility: epcr.toc_receiving_facility ?? epcr.destination_hospital,
      });
    }
  }

  async function removeSignature(id: string) {
    await db.signatures.delete(id);
    await enqueueSync('signatures', id, 'delete', { id });
    await loadSignatures();
  }

  const receivingSig = signatures.find(s => s.signer_role === 'receiving_clinician');
  const patientSig = signatures.find(s => s.signer_role === 'patient' || s.signer_role === 'guardian');
  const witnessSig = signatures.find(s => s.signer_role === 'witness');
  const emsSig = signatures.find(s => s.signer_role === 'ems_provider');

  const epcrPathBase = incidentId ? `/incidents/${incidentId}/epcr/${epcr.id}` : '';
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <DemoFillButton
          label="Fill TOC demo"
          onFill={() => onUpdate(demoTransferOfCareFields())}
        />
      </div>
      {/* Sprint K-Echoes-2: handoff context as read-only echoes so the
          receiving clinician sees what the field captured at a glance,
          and the EMT doesn't re-type values that are canonical
          upstream. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <UpstreamEcho
          label="Patient name"
          value={[epcr.patient_last_name, epcr.patient_first_name].filter(Boolean).join(', ') || null}
          editPath={epcrPathBase ? `${epcrPathBase}?tab=patient` : undefined}
          editLabel="Patient tab"
          locked={epcr.locked}
        />
        <UpstreamEcho
          label="Date of birth"
          value={epcr.patient_dob}
          editPath={epcrPathBase ? `${epcrPathBase}?tab=patient` : undefined}
          editLabel="Patient tab"
          locked={epcr.locked}
        />
        <UpstreamEcho
          label="Chief complaint"
          value={epcr.chief_complaint}
          editPath={epcrPathBase ? `${epcrPathBase}?tab=dispatch` : undefined}
          editLabel="Dispatch tab"
          locked={epcr.locked}
        />
        <UpstreamEcho
          label="Primary impression"
          value={epcr.primary_impression}
          editPath={epcrPathBase ? `${epcrPathBase}?tab=dispatch` : undefined}
          editLabel="Dispatch tab"
          locked={epcr.locked}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={20} className="text-brand-400" />
          <h3 className="text-lg font-semibold text-slate-200">Transfer of Care</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTocScript(true)}
            className="btn-primary gap-1 text-sm"
            title="Open the structured handoff script (MIST+ 6-bullet format)"
          >
            <ClipboardList size={14} /> Handoff Script
          </button>
          {incidentId && (
            <button
              onClick={() => navigate(`/incidents/${incidentId}/epcr/${epcr.id}/print`)}
              className="btn-secondary gap-1 text-sm"
            >
              <Printer size={14} /> Print TOC
            </button>
          )}
        </div>
      </div>

      {/* Context banner */}
      {!epcr.transport_decision && (
        <div className="rounded-lg border border-amber-700 bg-amber-950/30 p-3 text-sm text-amber-300 flex items-center gap-2">
          <AlertTriangle size={16} />
          Set a transport decision in the Disposition tab first. TOC workflow adapts to it.
        </div>
      )}

      {/* Receiving clinician (transports only) */}
      {isTransport && (
        <SignatureSection
          title="Receiving Clinician"
          description="Required signature at transfer of care. Document name, licensure, and time."
          cite="RI Protocol 1.02 §11 — transfer of care signature must be obtained"
          existing={receivingSig}
          role="receiving_clinician"
          onStart={() => {
            setNewSigner({ signer_name: '', signer_licensure: '', signer_role_title: '' });
            setShowSignaturePad('receiving_clinician');
          }}
          onRemove={removeSignature}
        />
      )}

      {/* Patient / refusal signature */}
      {isRefusal && (
        <>
          <SignatureSection
            title="Patient (or Guardian) Signature"
            description="Required for refusal of care or transport. Documents informed refusal."
            cite="RI Protocol 6.04 — Refusal of Patient Care or Transport"
            existing={patientSig}
            role="patient"
            onStart={() => {
              setNewSigner({ signer_name: '', signer_licensure: '', signer_role_title: '' });
              setShowSignaturePad('patient');
            }}
            onRemove={removeSignature}
          />
          <SignatureSection
            title="Witness Signature"
            description="Recommended for refusal scenarios (family member, law enforcement, hospital staff)."
            cite="RI Protocol 6.04"
            existing={witnessSig}
            role="witness"
            optional
            onStart={() => {
              setNewSigner({ signer_name: '', signer_licensure: '', signer_role_title: '' });
              setShowSignaturePad('witness');
            }}
            onRemove={removeSignature}
          />
        </>
      )}

      {/* EMS provider signature */}
      <SignatureSection
        title="EMS Provider Signature"
        description="The primary care provider for this call. Identity, cert level, and agency."
        cite="Internal — provider accountability"
        existing={emsSig}
        role="ems_provider"
        optional
        onStart={() => {
          setNewSigner({
            signer_name: user?.full_name ?? '',
            signer_licensure: user?.cert_level ?? '',
            signer_role_title: '',
          });
          setShowSignaturePad('ems_provider');
        }}
        onRemove={removeSignature}
      />

      {/* Delayed delivery (§10.3) */}
      <section className="card space-y-3">
        <header className="flex items-center gap-2">
          <Clock size={16} className="text-slate-400" />
          <span className="font-semibold text-slate-200">Report Delivery</span>
        </header>
        <p className="text-xs text-slate-400">
          Per RI Protocol 1.02 §4, a completed PCR must be provided to the receiving facility
          proximate to arrival, and no later than 2 hours after handoff.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs text-slate-400">Delivery method</label>
            <select
              value={epcr.toc_delivery_method ?? ''}
              onChange={(e) => onUpdate({ toc_delivery_method: e.target.value || null })}
              className="input-field"
              disabled={epcr.locked}
            >
              <option value="">Select...</option>
              <option value="printed at handoff">Printed copy left at handoff</option>
              <option value="printed later">Printed copy delivered later</option>
              <option value="emailed">Emailed to receiving facility</option>
              <option value="faxed">Faxed to receiving facility</option>
              <option value="electronic">Electronic delivery (RIEMSIS/portal)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400">Confirmed received by facility?</label>
            <div className="flex gap-2 mt-1">
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  onClick={() => onUpdate({ toc_confirmed_received: v })}
                  disabled={epcr.locked}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-medium min-h-[2.5rem] transition-colors',
                    epcr.toc_confirmed_received === v
                      ? v ? 'bg-ems-green text-white' : 'bg-slate-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700',
                  )}
                >
                  {v ? 'Yes' : 'No'}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-400">
              Reason for delayed delivery (if applicable)
            </label>
            <textarea
              value={epcr.toc_delay_reason ?? ''}
              onChange={(e) => onUpdate({ toc_delay_reason: e.target.value || null })}
              className="input-field min-h-[3rem] resize-y"
              placeholder="e.g., High call volume, report completed post-call and delivered via email"
              disabled={epcr.locked}
            />
          </div>
        </div>
      </section>

      {/* Signature pad modal */}
      {showSignaturePad && (
        <SignatureCaptureModal
          role={showSignaturePad}
          newSigner={newSigner}
          setNewSigner={setNewSigner}
          onSign={(dataUrl) => saveSignature(dataUrl, showSignaturePad)}
          onCancel={() => setShowSignaturePad(null)}
        />
      )}

      {/* Handoff script (§10) — read-aloud MIST+ modal */}
      {showTocScript && (
        <TransferOfCareScript
          epcr={epcr}
          onClose={() => setShowTocScript(false)}
        />
      )}
    </div>
  );
}

function SignatureSection({
  title, description, cite, existing, role, optional, onStart, onRemove,
}: {
  title: string;
  description: string;
  cite: string;
  existing: Signature | undefined;
  role: Signature['signer_role'];
  optional?: boolean;
  onStart: () => void;
  onRemove: (id: string) => void;
}) {
  return (
    <section className="card">
      <div className="flex items-start gap-3">
        <div className={cn(
          'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border',
          existing ? 'border-ems-green bg-ems-green text-white' :
            optional ? 'border-slate-600' : 'border-ems-amber',
        )}>
          {existing && <Check size={14} />}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-slate-200">{title}</h4>
            {!optional && !existing && (
              <span className="rounded bg-amber-900 px-1.5 py-0.5 text-[10px] text-amber-300">REQUIRED</span>
            )}
            {optional && (
              <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400">OPTIONAL</span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-slate-400">{description}</p>
          <p className="text-[10px] italic text-slate-500 mt-1">{cite}</p>

          {existing ? (
            <div className="mt-3 rounded-lg border border-slate-700 bg-slate-800 p-3">
              <div className="flex items-start gap-3">
                <img
                  src={existing.signature_image_data_url}
                  alt="Signature"
                  className="h-20 bg-white rounded border border-slate-600"
                />
                <div className="flex-1 text-xs">
                  <div className="font-semibold text-slate-200">{existing.signer_name}</div>
                  {existing.signer_licensure && (
                    <div className="text-slate-400">
                      {existing.signer_licensure}
                      {existing.signer_role_title && ` — ${existing.signer_role_title}`}
                    </div>
                  )}
                  <div className="mt-1 text-slate-500">
                    Signed {new Date(existing.signed_at).toLocaleString('en-US')}
                  </div>
                </div>
                <button
                  onClick={() => onRemove(existing.id)}
                  className="rounded p-1 text-slate-500 hover:text-red-400"
                  title="Remove signature"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ) : (
            <button onClick={onStart} className="btn-primary gap-1 mt-3 text-sm">
              <PenLine size={14} /> Capture Signature
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function SignatureCaptureModal({
  role, newSigner, setNewSigner, onSign, onCancel,
}: {
  role: Signature['signer_role'];
  newSigner: { signer_name: string; signer_licensure: string; signer_role_title: string };
  setNewSigner: React.Dispatch<React.SetStateAction<typeof newSigner>>;
  onSign: (dataUrl: string) => void;
  onCancel: () => void;
}) {
  const needsLicensure = role === 'receiving_clinician';
  const canSign = newSigner.signer_name.trim().length > 0 && (!needsLicensure || newSigner.signer_licensure);

  const roleLabel =
    role === 'receiving_clinician' ? 'Receiving Clinician' :
    role === 'patient' ? 'Patient' :
    role === 'guardian' ? 'Guardian' :
    role === 'witness' ? 'Witness' :
    role === 'ems_provider' ? 'EMS Provider' : 'Signer';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-xl border border-slate-700 bg-slate-900 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCheck size={18} className="text-brand-400" />
            <h3 className="font-semibold text-slate-200">{roleLabel} Signature</h3>
          </div>
          <button onClick={onCancel} className="rounded p-1 hover:bg-slate-800">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400">Full name</label>
            <input
              type="text"
              value={newSigner.signer_name}
              onChange={(e) => setNewSigner({ ...newSigner, signer_name: e.target.value })}
              className="input-field"
              placeholder={role === 'patient' ? 'Patient legal name' : 'Full name'}
              required
            />
          </div>

          {needsLicensure && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-slate-400">Licensure *</label>
                <select
                  value={newSigner.signer_licensure}
                  onChange={(e) => setNewSigner({ ...newSigner, signer_licensure: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">Select...</option>
                  {LICENSURE_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Role / title</label>
                <select
                  value={newSigner.signer_role_title}
                  onChange={(e) => setNewSigner({ ...newSigner, signer_role_title: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select...</option>
                  {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        {canSign ? (
          <SignaturePad onCapture={onSign} onCancel={onCancel} />
        ) : (
          <div className="rounded-lg border border-dashed border-slate-700 bg-slate-800 p-4 text-center text-sm text-slate-500">
            Enter name{needsLicensure ? ' and licensure' : ''} to enable signature pad.
          </div>
        )}
      </div>
    </div>
  );
}
